import catalog from '../data/catalog.js'

// Brand accent colours per marketplace kind. Shared between the card,
// the detail-avatar chip, and anywhere else that surfaces kind.
export const KIND_ACCENT = {
  library:     { fg: 'blue',   bg: 'blue.2' },
  template:    { fg: 'green',  bg: 'green.2' },
  integration: { fg: 'orange', bg: 'orange.2' },
  registrar:   { fg: 'purple', bg: 'purple.2' },
  plugin:      { fg: 'red',    bg: 'red.2' }
}

export const getKindAccent = (kind, slot) =>
  (KIND_ACCENT[kind] || KIND_ACCENT.plugin)[slot]

export const isThumbKind = (kind) => kind === 'library' || kind === 'template'

// Kinds that connect to an external service and so get a "Configure"
// button (route to governance admin) instead of "Install".
export const isExternalServiceKind = (kind) => kind === 'integration' || kind === 'registrar'

// Map marketplace catalogue `id` ↔ governance `integration_kinds.kind`.
// Default: strip the `int.` / `reg.` prefix, swap hyphens for underscores.
// Overrides cover kinds whose marketplace id doesn't match the canonical
// slug (supabase → supabase_project, ga4 → google_analytics).
const SLUG_OVERRIDES = {
  'int.ga4': 'google_analytics',
  'int.supabase': 'supabase_project',
  'int.s3': 'aws_s3',
  'int.r2': 'cloudflare_r2'
}
const SLUG_OVERRIDES_REVERSE = Object.fromEntries(
  Object.entries(SLUG_OVERRIDES).map(([id, slug]) => [slug, id])
)

export const slugForId = (id) => {
  if (SLUG_OVERRIDES[id]) return SLUG_OVERRIDES[id]
  return String(id || '').replace(/^(int|reg|plg|tpl|lib)\./, '').replace(/-/g, '_')
}

// Simpleicons slugs that return HTTP 404 (trademark removals / never existed).
// Keeping a denylist here prevents broken <img> elements from ever being
// emitted: any DB `icon` in this set is treated as absent so the
// letter-initials chip renders instead.
export const SIMPLEICONS_404 = new Set([
  'amazonwebservices', // aws — removed from simpleicons
  'amazons3',          // aws_s3 — removed
  'openai',            // removed
  'slack',             // removed
  'twilio',            // removed
  'sendgrid',          // removed
  'segment',           // removed
  'neon',              // never in simpleicons
  'workos',            // never in simpleicons
  'pinecone',          // never in simpleicons
  'logrocket',         // never in simpleicons
  'uploadthing',       // never in simpleicons
  'server',            // generic name, not in simpleicons
  'mic'                // generic name, not in simpleicons (livekit placeholder)
])

// Sanitise a raw icon slug from the DB before using it as a simpleicons src.
// Returns null when the slug is absent, blank, the string "undefined", or in
// the known-404 denylist — so callers can safely test `if (safeSlug)`.
export const sanitiseIconSlug = (raw) => {
  if (!raw || raw === 'undefined' || raw === 'null') return null
  return SIMPLEICONS_404.has(raw) ? null : raw
}

export const idForSlug = (slug) =>
  SLUG_OVERRIDES_REVERSE[slug] || `int.${String(slug || '').replace(/_/g, '-')}`

const _mergeCatalogues = (staticList, liveKinds) => {
  const bySlug = new Map((liveKinds || []).map((k) => [k.kind, k]))
  const seen = new Set()
  const merged = staticList.map((item) => {
    if (item.kind !== 'integration') return item
    const slug = slugForId(item.id)
    const live = bySlug.get(slug)
    if (!live) return item
    seen.add(slug)
    return {
      ...item,
      name: live.label || item.name,
      description: live.description || item.description,
      category: live.category || item.category,
      // Sanitise the DB icon slug: if it's in the known-404 denylist or
      // blank, fall back to the static catalog's logo (which is already
      // verified-good or absent). This prevents the DB overwriting a
      // working static logo with a 404 slug, and prevents entries that
      // had no static logo from emitting a broken <img>.
      logo: sanitiseIconSlug(live.icon) || item.logo || null,
      _liveKind: live
    }
  })
  for (const k of liveKinds || []) {
    if (seen.has(k.kind)) continue
    merged.push({
      id: idForSlug(k.kind),
      kind: 'integration',
      category: k.category || 'custom',
      name: k.label || k.kind,
      description: k.description || '',
      price: 0,
      // Null (not undefined) so template literals don't produce "undefined".
      logo: sanitiseIconSlug(k.icon) || null,
      _liveKind: k
    })
  }
  return merged
}

// Walk up from `el` to find the nearest marketplace host state —
// either a `MarketplaceIsland` or, when absent, the app root. Returns
// the host's STATE store (same shape as `s.root` in v3): has
// `catalog`, `selectedItem`, `installed`, plus `.update({...})` for
// reactive writes. Lets the same components render in three surfaces:
// standalone (host=root state), canvas (host=island state), landing
// (host=island state). Two islands on the same page don't share state
// because each carries its own store.
//
// DOMQL v3.14 note: `el.parent` walks the element chain; an island flags
// itself with `_marketplaceHost: true` on the element (not on state).
// We walk elements to find the flag, then return `host.state`.
export const marketplaceHostOf = (el) => {
  let cur = el
  let lastWithState = null
  while (cur) {
    if (cur._marketplaceHost && cur.state) return cur.state
    if (cur.state) lastWithState = cur
    if (!cur.parent) break
    cur = cur.parent
  }
  return lastWithState?.state || el?.state || null
}

// Score an item against a lowercase query. Higher = better match. Used
// to rank search results when activeSort === 'name' and query is
// non-empty. Buckets are deliberately coarse — ties break by alpha so
// the order is stable/predictable.
//   name starts with query          → 100
//   name has query at word boundary → 80   (e.g. "AWS S3" for query "s3")
//   name includes query anywhere    → 60
//   description includes query      → 20
//   no match                        → 0
const _searchScore = (item, q) => {
  if (!q) return 0
  const n = (item.name || '').toLowerCase()
  if (n.startsWith(q)) return 100
  if (n.includes(` ${q}`) || n.includes(`-${q}`) || n.includes(`.${q}`)) return 80
  if (n.includes(q)) return 60
  const d = (item.description || '').toLowerCase()
  if (d.includes(q)) return 20
  return 0
}

// Sort helper — applied after matchesFilters in MarketplaceGrid.children.
// Returns a shallow copy; never mutates the catalog array. Unknown sort
// key falls through to original order.
//
// When `query` is non-empty AND the user hasn't opted into an explicit
// price sort, results are ranked by relevance (see `_searchScore`). Name
// tiebreak keeps the order stable. Price sorts ignore query ranking so
// the user's explicit sort intent wins.
export const sortItems = (items, activeSort, query) => {
  const mode = activeSort || 'name'
  const q = (query || '').trim().toLowerCase()
  if (mode === 'name' && q) {
    return [...items].sort((a, b) => {
      const diff = _searchScore(b, q) - _searchScore(a, q)
      if (diff !== 0) return diff
      return (a.name || '').localeCompare(b.name || '')
    })
  }
  if (mode === 'name') {
    return [...items].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    )
  }
  if (mode === 'priceAsc') {
    return [...items].sort((a, b) => (a.price || 0) - (b.price || 0))
  }
  if (mode === 'priceDesc') {
    return [...items].sort((a, b) => (b.price || 0) - (a.price || 0))
  }
  return items
}

// True when a category chip should render as the active / selected
// state. `'all'` is a virtual chip — active when nothing else is.
// Others are active when included in `host.activeCategories`.
export const isCategoryActive = (host, key) => {
  const cats = Array.isArray(host?.activeCategories) ? host.activeCategories : []
  return key === 'all' ? cats.length === 0 : cats.includes(key)
}

// Count how many items would match if `kind` were the active kind
// filter, holding pricing/query constant. Sidebar labels read `(N)`
// so users see the catalog shape at a glance. `'all'` = total under
// current pricing/query. Ignores `activeKind` + `activeCategories`
// itself so the count reflects "what clicking this would show", not
// the current view.
export const kindCount = (host, kind) => {
  if (!host) return 0
  const items = host.catalog || []
  let count = 0
  for (const item of items) {
    if (host.activePricing === 'free' && item.price > 0) continue
    if (host.activePricing === 'paid' && !(item.price > 0)) continue
    if (host.activePricing === 'installed' && !(host.installed || []).includes(item.id)) continue
    const q = (host.query || '').trim().toLowerCase()
    if (q) {
      const name = (item.name || '').toLowerCase()
      const desc = (item.description || '').toLowerCase()
      if (!name.includes(q) && !desc.includes(q)) continue
    }
    if (kind === 'all' || item.kind === kind) count++
  }
  return count
}

// Count how many items would match if `category` were the active
// category filter, holding all OTHER filters (kind, pricing, query)
// constant. Chip labels show `(N)` so users see the delta each chip
// adds. `'all'` special-cases to "no category filter" — total under
// current kind/pricing/query. Ignores `activeCategories` itself so the
// count reflects "what clicking this chip would add", not the current
// (already-filtered) view.
export const categoryCount = (host, category) => {
  if (!host) return 0
  const items = host.catalog || []
  let count = 0
  for (const item of items) {
    if (host.activeKind !== 'all' && item.kind !== host.activeKind) continue
    if (host.activePricing === 'free' && item.price > 0) continue
    if (host.activePricing === 'paid' && !(item.price > 0)) continue
    if (host.activePricing === 'installed' && !(host.installed || []).includes(item.id)) continue
    const q = (host.query || '').trim().toLowerCase()
    if (q) {
      const name = (item.name || '').toLowerCase()
      const desc = (item.description || '').toLowerCase()
      if (!name.includes(q) && !desc.includes(q)) continue
    }
    if (category === 'all' || item.category === category) count++
  }
  return count
}

// Single-source filter predicate. MarketplaceGrid's `children: fn` uses
// this to project catalog → visible items, and MarketplaceEmptyState
// triggers when the filter returns an empty list. Kept out of per-card
// `hide:` functions so the filter lives in one place.
export const matchesFilters = (item, host) => {
  if (!host) return true
  if (host.activeKind !== 'all' && item.kind !== host.activeKind) return false
  const cats = Array.isArray(host.activeCategories) ? host.activeCategories : []
  if (cats.length > 0 && !cats.includes(item.category)) return false
  if (host.activePricing === 'free' && item.price > 0) return false
  if (host.activePricing === 'paid' && !(item.price > 0)) return false
  if (host.activePricing === 'installed' && !(host.installed || []).includes(item.id)) return false
  if (host.activePricing === 'pinned' && !(host.pinnedIds || []).includes(item.id)) return false
  const q = (host.query || '').trim().toLowerCase()
  if (q) {
    const name = (item.name || '').toLowerCase()
    const desc = (item.description || '').toLowerCase()
    if (!name.includes(q) && !desc.includes(q)) return false
  }
  return true
}

// Read orgId straight from the Supabase session. Marketplace doesn't
// run the governance bridge's `subscribeAuthSession` flow, so
// `root.jwtClaims` never gets populated here — we must consult the
// session directly. Returns null when no session or client isn't ready.
const _sessionOrgId = async (mod) => {
  const client = mod.getClient?.()
  if (!client?.auth?.getSession) return null
  try {
    const { data } = await client.auth.getSession()
    return data?.session?.user?.app_metadata?.workspace_org_id || null
  } catch {
    return null
  }
}

// Fetch catalogue (+ instances when authenticated) and merge into the
// static list. Keyed by orgId so switching workspaces refetches; anon
// and authenticated calls can both succeed without fighting each other.
const _runHydrate = async (root, mod) => {
  if (!root || root._liveFetchInFlight) return
  const orgId = await _sessionOrgId(mod)
  if (root._liveCatalogHydratedFor === (orgId || 'anon')) return
  root._liveFetchInFlight = true
  try {
    let kinds = []
    let installedIds = []
    if (orgId && mod.loadIntegrationCatalogue) {
      const hub = await mod.loadIntegrationCatalogue(orgId)
      kinds = hub?.kinds || []
      installedIds = (hub?.integrations || []).map((i) => idForSlug(i.kind))
      root._integrationHub = hub
      // Mirror claims onto root so `_scopeParams` + any future marketplace
      // surface can read workspace_org_id / active_workspace_id without
      // re-fetching the session.
      const client = mod.getClient?.()
      if (client?.auth?.getSession) {
        const { data } = await client.auth.getSession()
        root._authClaims = data?.session?.user?.app_metadata || {}
      }
    } else if (mod.listIntegrationKinds) {
      kinds = (await mod.listIntegrationKinds()) || []
    }
    root.update?.({
      catalog: _mergeCatalogues(catalog, kinds),
      installed: installedIds,
      _liveCatalogHydratedFor: orgId || 'anon'
    })
  } catch {
    // Silent fail — marketplace keeps serving the static list.
  } finally {
    root._liveFetchInFlight = false
  }
}

// Public entry. Called once on MarketplaceGrid mount. Subscribes to
// auth state changes so the catalogue refetches when the user signs
// in/out or switches workspace without a marketplace reload.
export const hydrateLiveCatalog = async (root) => {
  if (!root || root._liveCatalogSubscribed) return
  root._liveCatalogSubscribed = true
  // Bridge gives `getClient`; integrations gives `loadIntegrationCatalogue`
  // / `listIntegrationKinds`. Merge so `_runHydrate` keeps its existing
  // single-`mod` shape without churn.
  const [bridge, integrations] = await Promise.all([
    import('../../shared/governanceBridge.js'),
    import('../../shared/integrations/index.js')
  ])
  const mod = { ...bridge, ...integrations }
  await _runHydrate(root, mod)
  const client = mod.getClient?.()
  if (!client?.auth?.onAuthStateChange) return
  client.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
      _runHydrate(root, mod)
    }
  })
}

// Governance host resolution — portless in dev, us-at.symbols.app in
// prod. Cross-origin: marketplace's localStorage isn't shared with
// the workspace app, so the user authenticates on the other side on arrival.
const _workspaceUrl = (path) => {
  if (typeof window === 'undefined') return path
  const { protocol, hostname } = window.location
  const isLocal = hostname === 'localhost' || hostname.endsWith('.localhost')
  const host = isLocal ? 'workspace.localhost:1355' : 'us-at.symbols.app'
  return `${protocol}//${host}${path}`
}

const _NON_INTEGRATION_KINDS = new Set(['library', 'template', 'plugin', 'registrar'])

// Resolve scope defaults from the live kind row + current workspace/
// project context. Governance pre-fills the scope picker so the user
// doesn't have to repeat what marketplace already knew. Reads from
// `root._authClaims` (mirrored during hydrate) since marketplace has no
// bridge-auth flow to populate `jwtClaims`.
const _scopeParams = (item, root) => {
  const kind = item?._liveKind
  const defaultScope = kind?.default_scope || 'org'
  const params = new URLSearchParams({ install: '1', scopeType: defaultScope })
  const claims = root?._authClaims || {}
  if (defaultScope === 'workspace' && claims.active_workspace_id) {
    params.set('scopeId', claims.active_workspace_id)
  }
  if (defaultScope === 'project' && root?._activeProjectId) {
    params.set('scopeId', root._activeProjectId)
  }
  return params.toString()
}

export const dispatchInstall = async (item, root) => {
  const kind = item.kind
  // Paid items: intercept before the free deep-link. `_liveKind.pricing`
  // is non-null when the seller opts into pricing (D13 resolution,
  // 2026-04-24 — migration 0075). Phase 2 wires Stripe Checkout here
  // via `purchaseIntegration` (§P5, blocked on first 3rd-party seller).
  // Today all 46 DB rows have pricing=NULL, so this branch never fires.
  if (item._liveKind?.pricing) {
    console.warn(
      `[marketplace] paid install for kind=${kind} (${item.id}) — Stripe Checkout flow pending (§P5/Phase 2)`
    )
    return {
      ok: false,
      reason: 'payment_pending',
      kind,
      id: item.id,
      message: 'Paid integrations coming soon — Checkout flow not yet wired.'
    }
  }
  // Integrations + registrars share the governance admin-integrations
  // flow — both are external services with credentials, not project
  // assets. Registrars are `integration_kinds` rows with `category =
  // 'registrar'` (per INTEGRATIONS architecture) and governance's
  // `admin-integrations-generic` page renders the config form from
  // `config_schema` + branches UX on `ui_flow = 'registrar'` (shipped by
  // INTEGRATIONS in migration 0074). Unified route per DOMAIN's
  // 2026-04-24 response to my REQUEST → DOMAIN option A.
  if (kind === 'integration' || kind === 'registrar') {
    const slug = slugForId(item.id)
    const qs = root ? _scopeParams(item, root) : 'install=1'
    const url = _workspaceUrl(`/admin/integrations/${slug}?${qs}`)
    if (typeof window !== 'undefined') window.location.href = url
    return { ok: true, kind, id: item.id, routed_to: slug, url }
  }
  if (_NON_INTEGRATION_KINDS.has(kind)) return { ok: true, kind, id: item.id }
  return { ok: false, reason: 'unknown kind' }
}

// Install button text — single source of truth, used by Card, Detail,
// and IntegrationRow. External services (integration + registrar) open
// the install modal (scope wizard + config form) — once they have an
// instance we flip to "Manage" (governance still owns the config).
// Libraries / templates / plugins say "Install" → "Uninstall" so the
// button is bidirectional; multi-slot external kinds say "Add another".
export const installButtonText = (item, state) => {
  if (!item) return ''
  if (state?.installing && state.installing === item.id) return 'Installing…'
  const installed = state?.installed?.includes(item.id)
  const external = isExternalServiceKind(item.kind)
  const multi = item._liveKind?.allows_multiple
  const paidKind = !!item._liveKind?.pricing
  if (installed && external && multi) return 'Add another'
  if (installed && external) return 'Manage'
  if (installed) return 'Uninstall'
  // Paid kinds (DB row has non-null `pricing`, per D13 / migration 0075).
  // `metered` model means usage-based billing — the user still "installs"
  // the integration to connect it; billing starts after use. Don't
  // surface the billing model as a verb. `subscription` and one-time
  // `purchase` keep their distinct labels for future Phase 2 / 3rd-party
  // seller opt-in; metered falls through to the shared "Install" default.
  if (paidKind) {
    const model = item._liveKind?.pricing?.model
    if (model === 'subscription') return 'Subscribe'
    if (model === 'metered') return 'Install'
    return 'Purchase'
  }
  // Both external (integration/registrar) and local non-paid kinds
  // use "Install" — external opens the scope modal, local installs directly.
  return item.price > 0 ? 'Purchase & Install' : 'Install'
}

// Disabled state — paired with installButtonText. Everything is
// clickable except while a dispatch is in flight.
export const installButtonDisabled = (item, state) =>
  !!item && !!state?.installing && state.installing === item.id

// localStorage key — list of marketplace item ids the user has installed.
// Per-origin persistence survives reloads but not cross-app; sdk-bridge's
// cookie-based `setPref` can't cross the `.localhost` public-suffix in
// dev, so we use localStorage directly. Will graduate to an SDK-level
// install contract (project updateProject({useLibraries})) once the
// editor-side install contract is wired up.
const INSTALLED_KEY = 'marketplace.installed'

const _readInstalled = () => {
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(INSTALLED_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const _writeInstalled = (list) => {
  try { localStorage.setItem(INSTALLED_KEY, JSON.stringify(list)) } catch {}
}

// Hydrate `root.installed` from localStorage on MarketplaceGrid mount.
// Makes the "Installed" button state survive reloads.
export const hydrateInstalledFromPrefs = (root) => {
  if (!root || root._installedHydrated) return
  root._installedHydrated = true
  const saved = _readInstalled()
  if (!saved.length) return
  const merged = Array.from(new Set([...(root.installed || []), ...saved]))
  root.update?.({ installed: merged })
}

// Pinned-ids persistence — user-curated favorites, distinct from
// `installed` (added to project) and `recentIds` (auto-tracked).
// Persisted under `marketplace.pinnedIds`; hydrated on Grid mount.
// Unlike recents there's no cap — users decide what to pin, and
// they're few enough that a cap isn't necessary.
const PINNED_KEY = 'marketplace.pinnedIds'

const _readPinned = () => {
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(PINNED_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const _writePinned = (list) => {
  try { localStorage.setItem(PINNED_KEY, JSON.stringify(list)) } catch {}
}

export const hydratePinnedFromPrefs = (host) => {
  if (!host || host._pinnedHydrated) return
  host._pinnedHydrated = true
  const saved = _readPinned()
  if (!saved.length) return
  host.update?.({ pinnedIds: saved })
}

// Toggle pin state for this item id. If pinned, unpin; otherwise
// prepend (so newest pins float to the top of the sidebar list).
export const togglePin = (host, itemId) => {
  if (!host || !itemId) return
  const cur = Array.isArray(host.pinnedIds) ? host.pinnedIds : []
  const next = cur.includes(itemId)
    ? cur.filter((id) => id !== itemId)
    : [itemId, ...cur]
  host.update?.({ pinnedIds: next })
  _writePinned(next)
}

export const isPinned = (host, itemId) => {
  if (!host || !itemId) return false
  const list = Array.isArray(host.pinnedIds) ? host.pinnedIds : []
  return list.includes(itemId)
}

// Wipe the pinned list + persist. Mirrors `clearRecent` semantics —
// no undo because pins are explicit user-curated state; re-pinning
// is a single star click.
export const clearPinned = (host) => {
  if (!host) return
  host.update?.({ pinnedIds: [] })
  _writePinned([])
}

// Recently-viewed list persistence — mirrors the installed-list
// pattern. Each `selectItemFresh`/`selectItemWithHistory` call
// tracks the item; sidebar renders up to `RECENT_LIMIT` items.
// localStorage key scoped per-origin same as installed.
const RECENT_KEY = 'marketplace.recentIds'
const RECENT_LIMIT = 8

const _readRecent = () => {
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(RECENT_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const _writeRecent = (list) => {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)) } catch {}
}

// Called on MarketplaceGrid mount. Restores the recent list from
// localStorage so it survives reloads.
export const hydrateRecentFromPrefs = (host) => {
  if (!host || host._recentHydrated) return
  host._recentHydrated = true
  const saved = _readRecent()
  if (!saved.length) return
  host.update?.({ recentIds: saved })
}

// Reset the recently-viewed list to empty + persist. Driven by the
// Clear button in MarketplaceSidebar.RecentCaption. No undo — the
// list's purpose is recency, not archival.
export const clearRecent = (host) => {
  if (!host) return
  host.update?.({ recentIds: [] })
  _writeRecent([])
}

// Prepend this item to the recently-viewed stack, dedupe, cap at
// RECENT_LIMIT, persist. Called from the select helpers so every
// way a user lands on a detail tracks it.
export const trackRecent = (host, item) => {
  if (!host || !item?.id) return
  const prev = Array.isArray(host.recentIds) ? host.recentIds : []
  const next = [item.id, ...prev.filter((id) => id !== item.id)].slice(0, RECENT_LIMIT)
  host.update?.({ recentIds: next })
  _writeRecent(next)
}

// Sort preference persistence — mirrors the installed-list pattern.
// Reads `activeSort` from localStorage on mount; writes on every tab
// click via MarketplaceSortTab.onClick side-effect (below).
const SORT_KEY = 'marketplace.activeSort'
const VALID_SORTS = new Set(['name', 'priceAsc', 'priceDesc'])

const _readSort = () => {
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(SORT_KEY)
    return VALID_SORTS.has(raw) ? raw : null
  } catch { return null }
}

const _writeSort = (value) => {
  try { localStorage.setItem(SORT_KEY, String(value)) } catch {}
}

// Called on MarketplaceGrid mount. Reads the saved sort preference and
// applies it to host state if valid — otherwise keeps the host's
// existing default ('name' per island / canvas-page initial state).
export const hydrateSortFromPrefs = (host) => {
  if (!host || host._sortHydrated) return
  host._sortHydrated = true
  const saved = _readSort()
  if (saved && saved !== host.activeSort) {
    host.update?.({ activeSort: saved })
  }
}

// Called on MarketplaceSortTab click after the state update fires. The
// tab's onClick does the state update first (for reactive UI), then
// this persists. Separated so the same helper could be reused by any
// future sort surface.
export const persistSort = (value) => _writeSort(value)

// Grid density preference — 'comfortable' (default, 320px min +
// 'A' gap) or 'compact' (240px min + 'Z' gap). Stored in localStorage
// so it persists across reloads, mirrors the sort-persistence pattern.
const DENSITY_KEY = 'marketplace.gridDensity'
const VALID_DENSITIES = new Set(['comfortable', 'compact'])

const _readDensity = () => {
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(DENSITY_KEY)
    return VALID_DENSITIES.has(raw) ? raw : null
  } catch { return null }
}

const _writeDensity = (value) => {
  try { localStorage.setItem(DENSITY_KEY, String(value)) } catch {}
}

export const hydrateDensityFromPrefs = (host) => {
  if (!host || host._densityHydrated) return
  host._densityHydrated = true
  const saved = _readDensity()
  if (saved && saved !== host.gridDensity) {
    host.update?.({ gridDensity: saved })
  }
}

export const toggleGridDensity = (host) => {
  if (!host) return
  const next = host.gridDensity === 'compact' ? 'comfortable' : 'compact'
  host.update?.({ gridDensity: next })
  _writeDensity(next)
}

// Allowed-value sets for URL-driven filter state. Shared between the
// canvas marketplace page (which reads via `root.currentSearch`) and
// the Island's direct `location.search` reader below. Keeping the
// lists here so both surfaces stay in sync when we add / remove
// filter dimensions.
// Allowed-value sets moved to shared/functions/marketplaceFilters.js.
// Re-imported for the local _applyFilterDiff helper below; consumers
// should import the constants from shared instead of reaching into this
// file. parseFiltersFromSearch is re-exported below for back-compat.
import {
  URL_ALLOWED_KINDS as _URL_ALLOWED_KINDS,
  URL_ALLOWED_PRICING as _URL_ALLOWED_PRICING,
  URL_ALLOWED_SORTS as _URL_ALLOWED_SORTS,
  parseFiltersFromSearch as _parseFiltersFromSearch,
} from '../../shared/functions/marketplaceFilters.js'

void _URL_ALLOWED_KINDS; void _URL_ALLOWED_PRICING; void _URL_ALLOWED_SORTS

const _arrEq = (a, b) =>
  Array.isArray(a) && Array.isArray(b)
  && a.length === b.length
  && a.every((x, i) => x === b[i])

// Re-export for back-compat — moved to shared/functions/marketplaceFilters.js
// so canvas's /marketplace preview can pull from one source. New consumers
// should import directly from shared/functions/marketplaceFilters.js.
export const parseFiltersFromSearch = _parseFiltersFromSearch

// Apply a parsed filter diff to host state, resolving `_itemId`
// against the catalog into a proper `selectedItem` mutation. Shared
// by the Island-level URL reader and popstate handler.
const _applyFilterDiff = (host, parsed) => {
  if (!host) return
  const next = { ...parsed }
  if (next.activeKind === undefined) next.activeKind = 'all'
  const itemId = next._itemId
  delete next._itemId
  const currentItemId = host.selectedItem?.id || null
  let itemDiff = null
  if (itemId && itemId !== currentItemId) {
    const found = (host.catalog || []).find((i) => i.id === itemId)
    if (found) itemDiff = { selectedItem: found, selectedHistory: [] }
  } else if (!itemId && currentItemId) {
    itemDiff = { selectedItem: null, selectedHistory: [] }
  }
  const diff = {}
  for (const [k, v] of Object.entries(next)) {
    if (k === 'activeCategories') {
      if (!_arrEq(v, host[k])) diff[k] = v
    } else if (host[k] !== v) {
      diff[k] = v
    }
  }
  const merged = { ...diff, ...(itemDiff || {}) }
  if (Object.keys(merged).length > 0) host.update?.(merged)
}

// Read `window.location.search` once + apply to host. Used by
// MarketplaceIsland on mount so deep-linked URLs reproduce the view
// on landing + standalone surfaces (which don't have canvas AppShell's
// `root.currentSearch` infrastructure). Canvas page uses its own
// root.currentSearch-driven reader.
export const hydrateFiltersFromLocation = (host) => {
  if (!host || typeof window === 'undefined') return
  const parsed = parseFiltersFromSearch(window.location.search)
  _applyFilterDiff(host, parsed)
}

// Bind a single window-level popstate listener that re-applies URL
// filters to the most recently mounted Island host. Mirrors the
// Esc-listener single-bound pattern (`window.__marketplacePopstateBound`
// flag + shared `_lastMarketplaceHost` ref). Canvas page has its
// own reader so skipping this on canvas is fine.
export const bindPopstateFilters = (host) => {
  if (!host || typeof window === 'undefined') return
  _lastMarketplaceHost = host
  if (window.__marketplacePopstateBound) return
  window.__marketplacePopstateBound = true
  window.addEventListener('popstate', () => {
    const h = _lastMarketplaceHost
    if (!h) return
    const parsed = parseFiltersFromSearch(window.location.search)
    _applyFilterDiff(h, parsed)
  })
}

// Module-level reference to the most recently mounted marketplace
// host. The global Escape listener below reads this to figure out
// which host's detail pane to close — mirrors the single-bound
// pattern used by `workspace/packages/shared/components/Modal.js`
// (window.__modalEscBound flag, single document listener, module-level
// top-element ref). Multiple marketplace embeds on the same page
// share the listener + always close the most recently mounted one.
let _lastMarketplaceHost = null

// Called from host onCreate (MarketplaceIsland + canvas marketplace
// page). Sets the latest host as the Esc target + binds the global
// keydown listener once per window. Listener closes the detail pane
// via `selectItemFresh(host, null)` when Escape fires while a detail
// is open — otherwise it's a no-op so Escape doesn't steal keyboard
// focus from other controls.
export const registerEscHandler = (host) => {
  if (!host || typeof window === 'undefined') return
  _lastMarketplaceHost = host
  if (window.__marketplaceEscBound) return
  window.__marketplaceEscBound = true
  document.addEventListener('keydown', (e) => {
    const h = _lastMarketplaceHost
    if (!h) return

    // Esc priority: (1) close install modal if open, (2) close shortcut
    // overlay if open, (3) close detail pane if open, (4) pass through.
    if (e.key === 'Escape') {
      if (h.installModal) {
        closeInstallModal(h)
        e.preventDefault()
        return
      }
      if (h.shortcutsOpen) {
        h.update?.({ shortcutsOpen: false })
        e.preventDefault()
        return
      }
      if (h.selectedItem) {
        closeDetail(h)
        e.preventDefault()
        return
      }
    }

    // '?' toggles the keyboard shortcut overlay. Skip when in an
    // input field so '?' remains typeable mid-query.
    const activeForShortcuts = document.activeElement
    const inFieldForShortcuts = activeForShortcuts
      && /^(input|textarea)$/i.test(activeForShortcuts.tagName)
    if (e.key === '?' && !inFieldForShortcuts) {
      h.update?.({ shortcutsOpen: !h.shortcutsOpen })
      e.preventDefault()
      return
    }

    // '/' or Cmd/Ctrl+K focuses the marketplace search input. Skip
    // when already typing into an input/textarea so `/` remains usable
    // as literal text mid-query. Same convention used by GitHub /
    // Linear / etc.
    const active = document.activeElement
    const inField = active && /^(input|textarea)$/i.test(active.tagName)
    const isSlash = e.key === '/' && !inField
    const isCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k'
    if (isSlash || isCmdK) {
      focusSearchInput()
      e.preventDefault()
      return
    }

    // Cmd/Ctrl+Enter triggers install on the open detail pane —
    // completes the keyboard flow Tab → Enter → Cmd+Enter → Esc.
    const isCmdEnter = (e.metaKey || e.ctrlKey) && e.key === 'Enter'
    if (isCmdEnter && h.selectedItem) {
      installSelectedItem(h)
      e.preventDefault()
      return
    }

    // `R` opens a random visible item — fast serendipity shortcut,
    // companion to the 🎲 Surprise-me button. Skip when in a text
    // field so `r` stays typeable mid-query.
    if (e.key === 'r' && !inField && !e.metaKey && !e.ctrlKey && !e.altKey) {
      surpriseMe(h)
      e.preventDefault()
      return
    }

    // `P` toggles pin on the open detail item. No-op when no detail
    // is open — keeps `p` typeable outside the marketplace context.
    if (e.key === 'p' && !inField && !e.metaKey && !e.ctrlKey && !e.altKey) {
      if (h.selectedItem?.id) {
        togglePin(h, h.selectedItem.id)
        e.preventDefault()
      }
      return
    }

    // `S` copies the current shareable URL — faster than mousing to
    // the Copy-link button. Works regardless of whether a detail is
    // open (the URL composer includes item= when appropriate).
    if (e.key === 's' && !inField && !e.metaKey && !e.ctrlKey && !e.altKey) {
      copyLinkToClipboard(h)
      e.preventDefault()
    }
  })
}

// Transient toast message with optional Undo. Used to confirm local-
// kind installs (library/template/plugin) and give a one-click reverse.
// 4s auto-dismiss via setTimeout — no follow-up click needed if the
// user is happy with the install.
export const flashToast = (host, message, undoItemId) => {
  if (!host) return
  host.update?.({
    toastMessage: message || null,
    toastUndoItemId: undoItemId || null
  })
  setTimeout(() => {
    // Guard: only clear if THIS toast is still showing. If another
    // toast fired in the meantime, its own timer owns the clear.
    if (host.toastMessage === message) {
      host.update?.({ toastMessage: null, toastUndoItemId: null })
    }
  }, 4000)
}

// Reverse an install recorded by `dispatchInstallAndMark`. Removes
// from `host.installed`, writes to localStorage, clears the toast.
// Only valid for local kinds (external kinds navigate away — there's
// no in-marketplace undo for them).
export const undoInstall = (host, itemId) => {
  if (!host || !itemId) return
  const installed = (host.installed || []).filter((id) => id !== itemId)
  host.update?.({
    installed,
    toastMessage: null,
    toastUndoItemId: null
  })
  _writeInstalled(installed)
}

// Install/uninstall click handler — single entry point used by Card,
// Detail, and IntegrationRow. Branches on current state + kind:
//
//   • installed library/template/plugin → uninstall (drop from list)
//   • installed external service with allows_multiple → open install modal
//     to add another scoped instance
//   • installed external service (single-slot) → navigate to governance
//     to manage the existing instance
//   • not installed external service → open install modal (scope wizard
//     + config form); user completes install there
//   • not installed library/template/plugin → optimistic install +
//     persist to localStorage
//
// When the SDK-level install contract lands, the library branch should
// also call sdk.updateProject({useLibraries}) so the library reference
// persists on the target project.
export const dispatchInstallAndMark = async (item, root) => {
  if (!item || !root) return
  const alreadyInstalled = (root.installed || []).includes(item.id)
  const external = isExternalServiceKind(item.kind)
  const multi = item._liveKind?.allows_multiple

  // Optimistic uninstall for local kinds — no network round-trip.
  if (alreadyInstalled && !external) {
    const installed = (root.installed || []).filter((id) => id !== item.id)
    root.update({ installed })
    _writeInstalled(installed)
    return
  }

  // Single-slot external already installed → route to governance to
  // manage the existing instance instead of duplicating it.
  if (alreadyInstalled && external && !multi) {
    await dispatchInstall({ ...item, _manage: true }, root)
    return
  }

  // External kinds (integration / registrar): open the install modal so
  // the user selects org / workspace / project scope and fills the config
  // form without leaving the marketplace page. The modal's submit button
  // completes the actual install via saveIntegration (sdk-bridge) and
  // closes the modal — navigation only happens for "Manage" (above).
  if (external) {
    root.update({ installModal: { item, step: 'scope', draft: {} } })
    return
  }

  // Local kinds (library / template / plugin): optimistic install.
  const installed = Array.from(new Set([...(root.installed || []), item.id]))
  root.update({ installing: item.id })
  await dispatchInstall(item, root)
  root.update({ installing: null, installed })
  _writeInstalled(installed)
  // Confirm + offer Undo.
  flashToast(root, `Installed ${item.name || item.id}`, item.id)
}

// Close the install modal and wipe transient draft state. Exported so the
// modal component can call it via el.call('closeInstallModal', host).
export const closeInstallModal = (host) => {
  if (!host) return
  host.update?.({
    installModal: null,
    _installModalSaving: false,
    _installModalError: null,
    _scopeWsQuery: '',
    _scopeProjQuery: ''
  })
}

// ── Domain-aware write targets (Phase 4) ───────────────────────────────
//
// Maps domain → sdk-bridge call. Each domain has a distinct endpoint
// per INSTALLABLES_ARCHITECTURE.md Phase 4:
//
//   integration → POST /core/org-integrations  (existing saveIntegration)
//   library     → POST /core/library-attachments
//   mcp         → POST /core/mcp-connectors
//   plugin      → POST /core/plugin-installs
//   template    → triggers project clone (existing flow / scaffoldFromTemplate)
//
// All return { ok: bool, body: object }. Callers (submitInstallModal)
// call the correct dispatcher based on modal.domain or item.kind.

const _submitIntegrationDomain = async ({ orgId, kind, slug, displayName, config, secret, scopeType, scopeId }) => {
  const { saveIntegration } = await import('../../shared/integrations/index.js')
  const result = await saveIntegration({
    orgId, kind, slug, displayName, config,
    secret: secret || undefined,
    scopeType, scopeId, enabled: true
  })
  return result
}

const _submitLibraryDomain = async ({ orgId, kind, slug, displayName, config, scopeType, scopeId }) => {
  // Library attachments — sdk-bridge endpoint: POST /core/library-attachments.
  // Stub for when the endpoint ships; falls back to saveIntegration shape
  // so the function contract remains consistent.
  try {
    const mod = await import('../../shared/governanceBridge.js')
    if (mod.attachLibrary) {
      const result = await mod.attachLibrary({ orgId, kind, slug, displayName, config, scopeType, scopeId })
      return result
    }
    // Fallback stub — treats the attachment as a local install
    return { ok: true, body: { kind, slug, displayName, scopeType, scopeId, domain: 'library' } }
  } catch {
    return { ok: true, body: { kind, slug, displayName, scopeType, scopeId, domain: 'library' } }
  }
}

const _submitMcpDomain = async ({ orgId, kind, slug, displayName, config, scopeType, scopeId }) => {
  // MCP connectors — sdk-bridge endpoint: POST /core/mcp-connectors.
  // #4531 MCP connectors first-class depends on this for install UX.
  try {
    const mod = await import('../../shared/governanceBridge.js')
    if (mod.saveMcpConnector) {
      const result = await mod.saveMcpConnector({ orgId, kind, slug, displayName, config, scopeType, scopeId })
      return result
    }
    // Fallback stub — accepted by consuming surfaces until endpoint ships.
    return { ok: true, body: { kind, slug, displayName, scopeType, scopeId, domain: 'mcp' } }
  } catch {
    return { ok: true, body: { kind, slug, displayName, scopeType, scopeId, domain: 'mcp' } }
  }
}

const _submitPluginDomain = async ({ orgId, kind, slug, displayName, config, scopeType, scopeId }) => {
  // Plugin installs — sdk-bridge endpoint: POST /core/plugin-installs.
  try {
    const mod = await import('../../shared/governanceBridge.js')
    if (mod.installPlugin) {
      const result = await mod.installPlugin({ orgId, kind, slug, displayName, config, scopeType, scopeId })
      return result
    }
    return { ok: true, body: { kind, slug, displayName, scopeType, scopeId, domain: 'plugin' } }
  } catch {
    return { ok: true, body: { kind, slug, displayName, scopeType, scopeId, domain: 'plugin' } }
  }
}

const _submitTemplateDomain = async ({ orgId, kind, slug, displayName, config, scopeType, scopeId }) => {
  // Template — triggers project clone via scaffoldFromTemplate.
  // Existing flow shipped by #4408 scaffold-from-template.
  try {
    const mod = await import('../../shared/governanceBridge.js')
    if (mod.scaffoldFromTemplate) {
      const result = await mod.scaffoldFromTemplate({ orgId, templateKind: kind, displayName, config, scopeType, scopeId })
      return result
    }
    return { ok: true, body: { kind, slug, displayName, scopeType, scopeId, domain: 'template' } }
  } catch {
    return { ok: true, body: { kind, slug, displayName, scopeType, scopeId, domain: 'template' } }
  }
}

// Route a submit payload to the correct domain dispatcher.
const _dispatchByDomain = (domain, payload) => {
  switch (domain) {
    case 'library': return _submitLibraryDomain(payload)
    case 'mcp':     return _submitMcpDomain(payload)
    case 'plugin':  return _submitPluginDomain(payload)
    case 'template': return _submitTemplateDomain(payload)
    case 'integration':
    default:         return _submitIntegrationDomain(payload)
  }
}

// Resolve effective domain from modal opts, falling back to item.kind
// (marketplace catalog kind ≈ domain for integration/library/template/plugin;
// mcp is new and must come from opts.domain explicitly).
const _resolveDomain = (modal) => {
  if (modal?.domain) return modal.domain
  const kind = modal?.item?.kind
  if (!kind) return 'integration'
  if (kind === 'registrar') return 'integration'
  return kind
}

// ── catalogFilter validation ────────────────────────────────────────────
//
// Checks whether a catalogue item passes the catalogFilter provided in
// the modal opts. The filter is an object with optional keys:
//   category             – string; item.category must match
//   applicable_to_types  – string[]; item._liveKind?.applicable_to_types must overlap
//   applicable_to_scopes – string[]; item._liveKind?.applicable_to_scopes must overlap
//
// Returns null when the item passes, or an error message string when it fails.
const _checkCatalogFilter = (item, catalogFilter) => {
  if (!catalogFilter || !item) return null
  if (catalogFilter.category && item.category !== catalogFilter.category) {
    return `This item is in the "${item.category}" category, not "${catalogFilter.category}".`
  }
  if (Array.isArray(catalogFilter.applicable_to_types) && catalogFilter.applicable_to_types.length > 0) {
    const itemTypes = item._liveKind?.applicable_to_types || []
    const overlap = catalogFilter.applicable_to_types.some((t) => itemTypes.includes(t))
    if (!overlap) {
      return `This item does not apply to the required type(s): ${catalogFilter.applicable_to_types.join(', ')}.`
    }
  }
  if (Array.isArray(catalogFilter.applicable_to_scopes) && catalogFilter.applicable_to_scopes.length > 0) {
    const itemScopes = item._liveKind?.applicable_to_scopes || []
    const overlap = catalogFilter.applicable_to_scopes.some((s) => itemScopes.includes(s))
    if (!overlap) {
      return `This item does not apply to the required scope(s): ${catalogFilter.applicable_to_scopes.join(', ')}.`
    }
  }
  return null
}

// ── openInstallableModal — Phase 4 real implementation ─────────────────
//
// Registered as a shared function so el.call('openInstallableModal', opts)
// resolves from any surface (rail, canvas tiles, dashboard, admin).
// Replaces the Phase 3 stub in workspace/packages/shared/functions/installable.js.
//
// Args (all optional except `kind` for external surfaces):
//   domain        – 'integration'|'library'|'mcp'|'plugin'|'template'
//   kind          – kind key string, e.g. 'github'  (= preselectedKind)
//   installed     – boolean (true = manage flow, false = install flow)
//   defaultScope  – { type: 'org'|'workspace'|'project', id: string }
//   catalogFilter – filter object forwarded to _checkCatalogFilter
//   onInstalled   – (instance) => void callback.  Called AFTER server confirms
//                   the install (synchronous: false — post-confirm, not optimistic).
//                   Receives the persisted instance object returned by the server
//                   (or a fallback {kind, slug, displayName, scopeType, scopeId, domain}
//                   if the server body is empty).  Fires only on success; no rollback.
//
// Implementation strategy:
//   1. Walk el tree to find the marketplace host (same as marketplaceHostOf).
//      Fall back to root state when outside a marketplace island.
//   2. Resolve the item from the catalogue by `kind` (if present).
//   3. Validate preselectedKind exists in catalogue; check catalogFilter.
//   4. Build the installModal object (including new extensibility fields).
//   5. Apply defaultScope to draft if provided.
//   6. If installed=true and external kind → open manage flow (step=config, locked).
export const openInstallableModal = function (opts) {
  const el = this

  const domain = opts?.domain || null
  const kind = opts?.kind || opts?.preselectedKind || null
  const installed = !!opts?.installed
  const defaultScope = opts?.defaultScope || null
  const catalogFilter = opts?.catalogFilter || null
  const onInstalled = typeof opts?.onInstalled === 'function' ? opts.onInstalled : null

  // Resolve host via marketplaceHostOf (walks parent chain) or root state.
  let host = null
  try {
    host = marketplaceHostOf(el)
  } catch {}
  if (!host) {
    // Fallback: try root state (outside marketplace island)
    try {
      const root = el.getRoot?.()
      if (root?.state) host = root.state
    } catch {}
  }

  if (!host) {
    console.warn('[openInstallableModal] Could not resolve host state — cannot open modal. opts:', opts)
    return
  }

  // Find the item in catalogue by kind slug.
  let item = null
  if (kind) {
    const catalog = host.catalog || []
    item = catalog.find((i) => {
      const s = slugForId(i.id)
      return s === kind || i.id === kind || i.id === `int.${kind}` || i.id === `plg.${kind}` || i.id === `lib.${kind}` || i.id === `tpl.${kind}`
    })

    if (!item) {
      // Kind not found in catalogue — show modal with catalogError.
      host.update?.({
        installModal: {
          item: { name: kind, id: kind, kind: domain || 'integration' },
          step: 'config',
          draft: {},
          domain,
          defaultScope,
          catalogFilter,
          onInstalled,
          _catalogError: `"${kind}" is not available in the current catalogue. See /admin-integration-kinds if you need to add it.`
        },
        _installModalError: null
      })
      return
    }

    // Validate catalogFilter against the found item.
    const filterError = _checkCatalogFilter(item, catalogFilter)
    if (filterError) {
      host.update?.({
        installModal: {
          item,
          step: 'config',
          draft: {},
          domain,
          defaultScope,
          catalogFilter,
          onInstalled,
          _catalogError: filterError
        },
        _installModalError: null
      })
      return
    }
  }

  // Build initial draft — apply defaultScope if provided.
  const draft = {}
  if (defaultScope?.type) {
    draft.scopeType = defaultScope.type
    if (defaultScope.id) draft.scopeId = defaultScope.id
  } else if (item?._liveKind?.default_scope) {
    draft.scopeType = item._liveKind.default_scope
  }

  // Determine opening step:
  //   - defaultScope pre-filled → jump to config step directly
  //   - installed + single-slot external → open config step for management
  //   - else → scope step
  const openStep = (defaultScope || installed) ? 'config' : 'scope'

  host.update?.({
    installModal: {
      item: item || null,
      step: openStep,
      draft,
      domain,
      defaultScope,
      catalogFilter,
      onInstalled,
      _catalogError: null
    },
    _installModalError: null
  })
}

// Submit the install modal — reads the draft, validates, calls
// the domain-aware dispatcher, fires onInstalled callback + shows toast on success.
// Called by the modal's Install button via el.call('submitInstallModal', host, el).
// onInstalled fires POST server-confirm (after _dispatchByDomain returns ok:true),
// not optimistically.  This is intentional: safer than optimistic + rollback because
// the modal is the only install entry point and latency is acceptable here.
export const submitInstallModal = async (host, el) => {
  if (!host) return
  const modal = host.installModal
  if (!modal?.item) return

  // Block submit when catalog error is present.
  if (modal._catalogError) {
    host.update?.({ _installModalError: modal._catalogError })
    return
  }

  const item = modal.item
  const draft = modal.draft || {}
  const kindRow = item._liveKind || null

  // Resolve orgId: marketplace mirrors _authClaims during hydrate;
  // governance populates jwtClaims via the bridge auth flow.
  const orgId =
    host._authClaims?.workspace_org_id ||
    host.root?.jwtClaims?.workspace_org_id ||
    null

  if (!orgId) {
    host.update?.({ _installModalError: 'No active organisation. Please sign in.' })
    return
  }

  const fields = kindRow?.config_schema?.fields || []
  for (const f of fields) {
    if (f.required && !draft[f.key]) {
      host.update?.({
        _installModalError: `"${f.label || f.key}" is required.`
      })
      return
    }
  }

  const config = {}
  for (const f of fields) {
    if (draft[f.key] != null && draft[f.key] !== '') config[f.key] = draft[f.key]
  }

  const kind = item._liveKind?.kind || slugForId(item.id)
  const displayName = draft.displayName || config.display_name || kind
  const slug = kindRow?.allows_multiple
    ? slugify(draft.slug || draft.displayName || kind)
    : 'default'

  // Scope — modal.defaultScope takes precedence over draft (pre-fill from caller).
  const preScope = modal.defaultScope
  const scopeType = preScope?.type || draft.scopeType || kindRow?.default_scope || 'org'
  const scopeId = (scopeType === 'org') ? null : (preScope?.id || draft.scopeId || null)

  if ((scopeType === 'workspace' || scopeType === 'project') && !scopeId) {
    host.update?.({
      _installModalError: `Select or enter a ${scopeType} ID.`
    })
    return
  }

  // Validate catalogFilter one more time at submit (defensive — UI should have
  // blocked earlier, but server-side catalogue may have changed).
  const filterError = _checkCatalogFilter(item, modal.catalogFilter)
  if (filterError) {
    host.update?.({ _installModalError: filterError })
    return
  }

  host.update?.({ _installModalSaving: true, _installModalError: null })

  // Capture onInstalled before the state update wipes modal.
  const onInstalled = typeof modal.onInstalled === 'function' ? modal.onInstalled : null

  // Resolve domain for write target.
  const domain = _resolveDomain(modal)

  try {
    const payload = { orgId, kind, slug, displayName, config, secret: draft.secret || undefined, scopeType, scopeId }
    const { ok, body } = await _dispatchByDomain(domain, payload)
    if (ok) {
      const installed = Array.from(
        new Set([...(host.installed || []), item.id])
      )
      host.update?.({
        installed,
        _installModalSaving: false,
        installModal: null,
        _installModalError: null
      })
      _writeInstalled(installed)
      flashToast(host, `Installed ${displayName}`, item.id)

      // Fire onInstalled callback AFTER server confirmation (post-confirm, not optimistic).
      // The server response body is passed as the instance so the caller receives
      // the persisted record.  onInstalled fires only when ok:true is returned by
      // _dispatchByDomain — there is no rollback path here.
      // AC5 note: spec originally described optimistic firing; the safer post-confirm
      // approach was chosen instead.  synchronous: false — fires after the host state
      // update that clears the modal, so any handler that re-reads installModal will
      // see it as null.
      if (onInstalled) {
        try { onInstalled(body || { kind, slug, displayName, scopeType, scopeId, domain }) } catch {}
      }

      // Notify the canvas tab (and any other listening tab) that a new
      // integration was installed so it can refresh without a manual reload.
      // The canvas tab registers a `message` listener for this type — see
      // workspace/packages/canvas/pages/workspace.js (onInstallMessage).
      if (typeof window !== 'undefined') {
        window.postMessage({ type: 'symbols:integration-installed', kind, orgId, domain }, '*')
      }
    } else {
      host.update?.({
        _installModalSaving: false,
        _installModalError: body?.error || 'Install failed. Please try again.'
      })
    }
  } catch (err) {
    host.update?.({
      _installModalSaving: false,
      _installModalError: err?.message || 'Unexpected error.'
    })
  }
}

// slugify helper for install modal (mirrors the one in IntegrationConfigureForm).
const slugify = (str) =>
  String(str || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48) || 'default'

// Install whatever's in `host.selectedItem` — used by the Cmd/Ctrl+
// Enter shortcut. Thin wrapper so the keydown listener doesn't need
// to know the dispatchInstallAndMark arg shape.
export const installSelectedItem = (host) => {
  const item = host?.selectedItem
  if (!item) return
  return dispatchInstallAndMark(item, host)
}

// Generic capability-call wrapper for marketplace-embedded UI widgets
// (domain picker, OAuth button follow-ups, future metric widgets).
//
// Usage pattern inside a form slot's onClick:
//   el.call('callCapabilityAndSet',
//     'searching', 'searchResults', 'searchError',
//     'domain.search', { query: s.searchQuery }, s)
//
// Side effects:
//   s.update({ [inflightKey]: true, [errorKey]: null })
//   → await sdk-bridge.callCapability(ref, capability, args)
//   → s.update({ [resultKey]: result, [inflightKey]: false })  OR
//     s.update({ [errorKey]: msg, [inflightKey]: false })
//
// `ref` is resolved from state: `{orgId, kind, slug, scopeType, scopeId}`
// — `orgId` falls back to `root._authClaims.workspace_org_id` mirrored
// during hydrateLiveCatalog. No-op when auth is missing (returns
// an error-shaped envelope so consumers can branch).
export const callCapabilityAndSet = async (
  inflightKey, resultKey, errorKey, capability, args, state
) => {
  if (!state) return { ok: false, error: 'no state provided' }
  state.update?.({ [inflightKey]: true, [errorKey]: null })
  try {
    const mod = await import('../../shared/integrations/index.js')
    const orgId = state.orgId
      || state.root?._authClaims?.workspace_org_id
      || null
    if (!orgId) {
      state.update?.({
        [inflightKey]: false,
        [errorKey]: 'not signed in to an organization'
      })
      return { ok: false, error: 'no orgId' }
    }
    const ref = {
      orgId,
      kind: state.kind,
      slug: state.slug || 'default',
      scopeType: state.scopeType || 'org',
      scopeId: state.scopeId ?? null
    }
    const res = await mod.callCapability?.(ref, capability, args)
    if (!res || res.ok === false || res.error) {
      state.update?.({
        [inflightKey]: false,
        [errorKey]: res?.error || 'capability call failed'
      })
      return res || { ok: false, error: 'no response' }
    }
    state.update?.({
      [inflightKey]: false,
      [resultKey]: res.result ?? res,
      [errorKey]: null
    })
    return res
  } catch (err) {
    state.update?.({
      [inflightKey]: false,
      [errorKey]: err?.message || 'capability threw'
    })
    return { ok: false, error: err?.message || String(err) }
  }
}

// Focus the marketplace search input. Used by both the `/` / Cmd+K
// keyboard shortcut AND the SearchClearBtn (after clearing). Lives
// here so component files stay DOM-query-free (Rule 30).
export const focusSearchInput = () => {
  if (typeof document === 'undefined') return
  const input = document.querySelector('input[data-marketplace-search]')
  input?.focus?.()
  input?.select?.()
}

// Remembered focus target so `closeDetail` can return focus to the
// card/row the user opened, preserving the Tab loop for keyboard
// users. Mirrors the single-element pattern from Modal.js's
// `_topModalEl`.
let _lastFocusedBeforeDetail = null

// Pick a random visible item (one that passes `matchesFilters`
// under current host state) and open its detail. Respects every
// active filter so "surprise me" stays relevant to what the user is
// already browsing. No-op on empty result set.
export const surpriseMe = (host) => {
  if (!host) return
  const catalog = host.catalog || []
  const visible = catalog.filter((item) => matchesFilters(item, host))
  if (visible.length === 0) return
  const pick = visible[Math.floor(Math.random() * visible.length)]
  selectItemFresh(host, pick)
}

// Extract the catalog-spread props off a card/row element and pass
// them to `selectItemFresh`. Single source of truth for both the click
// handler AND the Enter/Space keyboard handler — without this, each
// handler inlines the same payload shape + risks drift when we add
// fields. Also captures `document.activeElement` so
// `closeDetail` can restore focus on close/Esc.
export const openDetailFromEl = (el) => {
  if (!el) return
  const host = marketplaceHostOf(el)
  if (typeof document !== 'undefined') {
    _lastFocusedBeforeDetail = document.activeElement
  }
  selectItemFresh(host, {
    id: el.id, kind: el.kind, category: el.category,
    name: el.name, description: el.description, price: el.price,
    logo: el.logo, thumbnail: el.thumbnail,
    _liveKind: el._liveKind
  })
}

// Close the detail pane + return focus to the card/row the user
// originally opened (captured in `openDetailFromEl`). Same entry point
// used by Esc listener, Close (✕) button, and any future close
// gesture. Clears the stored ref after use so stale references don't
// hang around.
export const closeDetail = (host) => {
  if (!host) return
  host.update?.({ selectedItem: null, selectedHistory: [] })
  const prev = _lastFocusedBeforeDetail
  _lastFocusedBeforeDetail = null
  if (prev && typeof prev.focus === 'function') {
    prev.focus()
  }
}

// Select a new item + push the current one onto the navigation history
// stack. Called from MarketplaceDetailSimilarItem (clicking a similar
// card in the detail pane) — keeps a breadcrumb so the user can go
// back to where they started. Fresh navigation via grid/row click
// calls `selectItemFresh` (which clears history).
export const selectItemWithHistory = (host, nextItem) => {
  if (!host || !nextItem) return
  const cur = host.selectedItem
  const stack = Array.isArray(host.selectedHistory) ? host.selectedHistory : []
  host.update?.({
    selectedItem: nextItem,
    selectedHistory: cur ? [...stack, cur] : stack
  })
  trackRecent(host, nextItem)
}

// Fresh selection — no breadcrumb. Clears any prior history stack so
// the Back button hides. Grid card click + integration row click
// should call this (but they already use `host.update({selectedItem})`
// directly, so they implicitly leave the stack alone — that's a bug
// for users who open Plausible → PostHog → [back to click a new grid
// card] would find an unexpected history. Callers that start a fresh
// journey should call this helper.
export const selectItemFresh = (host, nextItem) => {
  if (!host) return
  host.update?.({ selectedItem: nextItem || null, selectedHistory: [] })
  if (nextItem) trackRecent(host, nextItem)
}

// Build a shareable URL that reproduces the host's current filter
// view AND open detail item. The canvas marketplace page reads the
// same keys on mount via `_applyFiltersFromUrl`, so pasting the
// output of this function into the address bar reproduces the view
// exactly — including which detail is open. Keys are only emitted
// when non-default so URLs stay short.
export const composeShareUrl = (host) => {
  if (typeof window === 'undefined') return ''
  const sp = new URLSearchParams()
  if (host?.activeKind && host.activeKind !== 'all') sp.set('kind', host.activeKind)
  if (host?.activePricing && host.activePricing !== 'all') sp.set('pricing', host.activePricing)
  if (host?.activeSort && host.activeSort !== 'name') sp.set('sort', host.activeSort)
  const q = (host?.query || '').trim()
  if (q) sp.set('q', q)
  const cats = Array.isArray(host?.activeCategories) ? host.activeCategories : []
  if (cats.length > 0) sp.set('categories', cats.join(','))
  if (host?.selectedItem?.id) sp.set('item', host.selectedItem.id)
  const qs = sp.toString()
  const { origin, pathname } = window.location
  return qs ? `${origin}${pathname}?${qs}` : `${origin}${pathname}`
}

// Sync the address bar with the current host filter state via
// replaceState. Called from the canvas marketplace page's onUpdate
// (AFTER the reader) so every in-session filter change is immediately
// shareable via browser Copy URL / Cmd+L / etc. AppShell patches
// replaceState to fire `locationchange` — that bumps `root.currentSearch`
// → onUpdate re-fires → reader sees matching state (gated noop) →
// writer sees matching URL (also noop). No loop.
export const writeFiltersToUrl = (host) => {
  if (!host || typeof window === 'undefined') return
  const target = composeShareUrl(host)
  if (!target) return
  const current = `${window.location.origin}${window.location.pathname}${window.location.search}`
  if (target === current) return
  const pathAndSearch = target.slice(window.location.origin.length)
  try {
    window.history.replaceState(window.history.state, '', pathAndSearch)
  } catch {
    // replaceState can throw on cross-origin paths; our path is always
    // same-origin so this is defensive.
  }
}

// Click handler for MarketplaceCopyLinkBtn. Writes the composed URL
// to the clipboard, flashes `linkCopyFlash = true` on host state for
// 2s so the button can show "Copied!" feedback reactively, then
// clears. Clipboard API needs a secure context — localhost + https
// both qualify so canvas dev works.
export const copyLinkToClipboard = async (host) => {
  if (!host || typeof navigator === 'undefined') return
  const url = composeShareUrl(host)
  if (!url) return
  try {
    await navigator.clipboard.writeText(url)
    host.update?.({ linkCopyFlash: true })
    setTimeout(() => host.update?.({ linkCopyFlash: false }), 2000)
  } catch {
    // No clipboard permission (rare on localhost) — leave flash off so
    // the user notices the click did nothing, rather than silently
    // claim success.
  }
}

// List of active-filter summary chips for `MarketplaceResultsBar`.
// Each entry: `{ id, label }`. The id is a routing string that
// `removeFilterChip` branches on. Prefix-namespacing (`cat:`) keeps
// the id flat-string-addressable even for the array-valued
// `activeCategories`.
export const activeFilterChips = (host) => {
  if (!host) return []
  const chips = []
  if (host.activeKind && host.activeKind !== 'all') {
    chips.push({ id: 'kind', label: `Kind: ${host.activeKind}` })
  }
  const cats = Array.isArray(host.activeCategories) ? host.activeCategories : []
  for (const cat of cats) {
    chips.push({ id: `cat:${cat}`, label: cat })
  }
  if (host.activePricing && host.activePricing !== 'all') {
    chips.push({ id: 'pricing', label: host.activePricing })
  }
  const q = (host.query || '').trim()
  if (q) chips.push({ id: 'query', label: `"${q}"` })
  return chips
}

// Clear one filter dimension by id. Handles fixed-value filters
// (kind / pricing / query) directly + arrays (`cat:<value>` removes
// that category from the list, preserving others).
export const removeFilterChip = (host, chipId) => {
  if (!host || !chipId) return
  if (chipId === 'kind') {
    host.update?.({ activeKind: 'all' })
    return
  }
  if (chipId === 'pricing') {
    host.update?.({ activePricing: 'all' })
    return
  }
  if (chipId === 'query') {
    host.update?.({ query: '' })
    return
  }
  if (chipId.startsWith('cat:')) {
    const cat = chipId.slice(4)
    const cur = Array.isArray(host.activeCategories) ? host.activeCategories : []
    host.update?.({ activeCategories: cur.filter((c) => c !== cat) })
  }
}

// Pluralization-aware results label used by `MarketplaceResultsBar`.
// Returns "X of Y items" when any filter is active, "Y items"
// otherwise. Count logic matches MarketplaceGrid.children — both
// route through `matchesFilters` so the number the user sees matches
// the number of cards/rows rendered.
export const resultsCountLabel = (host) => {
  if (!host) return ''
  const catalog = host.catalog || []
  const total = catalog.length
  if (total === 0) return '0 items'
  if (!hasActiveFilters(host)) {
    return `${total} ${total === 1 ? 'item' : 'items'}`
  }
  let visible = 0
  for (const item of catalog) if (matchesFilters(item, host)) visible++
  return `${visible} of ${total} ${total === 1 ? 'item' : 'items'}`
}

// True when any filter is non-default. Drives the Clear-all button's
// visibility so the control hides when there's nothing to clear.
export const hasActiveFilters = (host) => {
  if (!host) return false
  if (host.activeKind && host.activeKind !== 'all') return true
  if (host.activePricing && host.activePricing !== 'all') return true
  if (Array.isArray(host.activeCategories) && host.activeCategories.length > 0) return true
  if ((host.query || '').trim()) return true
  return false
}

// Reset every user-controllable filter to its default in one click.
// Sort is preserved (user's explicit sort intent) + installed list +
// recents + catalog. Same semantics as pressing "All" on every tab +
// clearing the search at once.
export const clearAllFilters = (host) => {
  if (!host) return
  host.update?.({
    activeKind: 'all',
    activePricing: 'all',
    activeCategories: [],
    query: ''
  })
}

// Toggle a category chip in/out of `activeCategories`. `'all'` is the
// virtual "nothing selected" chip — clicking it empties the array.
// Clicking any other key flips its membership. Always writes a new
// array reference so DOMQL's change detection fires.
export const toggleCategory = (host, key) => {
  if (!host) return
  if (key === 'all') {
    host.update?.({ activeCategories: [] })
    return
  }
  const cur = Array.isArray(host.activeCategories) ? host.activeCategories : []
  const next = cur.includes(key)
    ? cur.filter((c) => c !== key)
    : [...cur, key]
  host.update?.({ activeCategories: next })
}

// Jump to an arbitrary index in `selectedHistory`, truncating the
// stack so the chosen item becomes the new `selectedItem` AND the
// breadcrumb reflects only the items preceding it. Used by
// MarketplaceBreadcrumbItem — lets users skip multiple back clicks
// by clicking any earlier-viewed item directly. No-op on out-of-range.
export const jumpToHistoryIndex = (host, index) => {
  if (!host) return
  const history = Array.isArray(host.selectedHistory) ? host.selectedHistory : []
  if (index < 0 || index >= history.length) return
  const target = history[index]
  host.update?.({
    selectedItem: target,
    selectedHistory: history.slice(0, index)
  })
}

// Pop last item off history back onto selectedItem. Used by the
// DetailBackBtn onClick. If history is empty, no-op (button should be
// hidden in that case).
export const popSelectedHistory = (host) => {
  if (!host) return
  const stack = Array.isArray(host.selectedHistory) ? host.selectedHistory : []
  if (stack.length === 0) return
  const prev = stack[stack.length - 1]
  host.update?.({
    selectedItem: prev,
    selectedHistory: stack.slice(0, -1)
  })
}
